import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import { Drawer, useMediaQuery } from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Color from "../../../../assets/colors";
import { openEmployerSignUp } from "../../../../helpers/elp_sign_up";
import { handleSwitchCountry } from "../../../../helpers/link_management";
import { setEmployerParams } from "../../../../helpers/multicountry_management";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import { getUtmQueryString } from "../../../../helpers/utm";
import {
  checkAuthentication,
  checkPDPAStatus,
  hydrateUserObject,
  newGetUserInfo,
} from "../../../../redux/actions/user_action";
import * as types from "../../../../redux/types/user_type";
import WithOnboarding from "../../../hoc/WithOnboarding";
import SharedSnackbar from "../../SharedSnackbar/SharedSnackbar";
import CustomBreadcrumbs from "./Breadcrumb";
import {
  AppbarStyled,
  CTAContainer,
  ChevronDownStyled,
  ChevronUpStyled,
  ClickAwayListenerStyled,
  CountryGrowStyled,
  CountryMenuItem,
  CountryOptionName,
  CountryText,
  DividerLine,
  DrawerContentContainer,
  DrawerCountryText,
  DrawerCountryToggleGrid,
  DrawerDropdownRadio,
  DrawerLoginButton,
  DrawerSignUpButton,
  ELPDrawerHiredlyLogo,
  ELPDrawerHiredlyLogoSG,
  ELPHiredlyLogo,
  ELPHiredlyLogoSG,
  HorizontalDividerLine,
  IconButtonStyled,
  LinkContainer,
  LinkStyled,
  LocationContainer,
  MYCountryFlag,
  MenuListStyled,
  NavbarContentContainer,
  NavbarLinksStyled,
  PaperStyled,
  PopperStyled,
  SGCountryFlag,
  StyledLoginButton,
  StyledMapPin,
  StyledMenuText,
  StyledOutlinedButton,
  StyledSignUpButton,
  ToolbarStyled,
  VanillaGrid,
} from "./styles";

function EmployerNavbar(props) {
  const { page, breadcrumbItems } = props;
  const isMobile = useMediaQuery("(max-width: 640px)");
  const countryAnchorRef = useRef(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCountryPopper, setOpenCountryPopper] = useState(false);
  const [showSearchbar, setShowSearchbar] = useState(false);

  const hidePostForFree = localStorage.getItem("posthogImage") == "hidden";

  // TODO: write function for redux hydration for the user profile
  const reduxUser = useSelector(
    (state) => {
      const { user } = state?.user || {};
      const { name, email, pdpaAccepted, profileImage, id } = user;

      if (Object.keys(user).length < 1) {
        return {};
      }

      return { name, email, pdpaAccepted, profileImage, id };
    },
    (prev, next) => {
      return (
        prev.name === next.name &&
        prev.email === next.email &&
        prev.pdpaAccepted === next.pdpaAccepted &&
        prev.profileImage === next.profileImage &&
        prev.id === next.id
      );
    }
  );

  const dispatch = useDispatch();

  const newTagKey = `hasSeenCurrentSalaryTag_${reduxUser?.id}`;

  // breakpoint
  const desktopSize = useMediaQuery("(min-width: 1280px)");
  const tabletSize = useMediaQuery("(min-width: 768px)");

  // adding this for hiredly logo to align center when screen is too big
  const mobileLSize = useMediaQuery("(max-width: 767px)");
  const mobileSize = useMediaQuery("(max-width: 450px)");

  const router = useRouter();

  const urlSubdomain = window?.location?.hostname
    .split(".")
    .at(0)
    .toString()
    .toLowerCase();

  function getEnvCountry() {
    return urlSubdomain.includes("my")
      ? "MY"
      : urlSubdomain.includes("sg")
      ? "SG"
      : "TEST";
  }

  const envCountry = getEnvCountry();

  // Perform redux hydration before attempt to fetch user info
  async function handleRecallGetUser() {
    const localStorageUser = localStorage.getItem(types.USER_PROFILE);

    if (localStorageUser) {
      return dispatch(hydrateUserObject(JSON.parse(localStorageUser)));
    }

    // I THINK I FOUND OUT WHY. MUST ALSO CHECK IF LOCALSTORAGE IS NOT FALSEY AS WELL, OTHERWISE IT WILL ALWAYS GET NEW USER INFO
    if (Object.keys(reduxUser ?? {}).length <= 0) {
      return dispatch(newGetUserInfo());
    }

    return;
  }
  // recall get user info api if redux not already populated
  useEffect(() => {
    if (checkAuthentication()) {
      handleRecallGetUser();

      if (!reduxUser.pdpaAccepted && reduxUser.pdpaAccepted !== undefined) {
        dispatch(checkPDPAStatus());
      }
    }

    if (
      (localStorage?.getItem("apply_origin") ||
        localStorage?.getItem("bulkApplyFromSimilarJob") ||
        localStorage?.getItem("bulkApplyJobId")) &&
      !router.asPath.includes("/apply")
    ) {
      localStorage.removeItem("apply_origin");
      localStorage.removeItem("bulkApplyFromSimilarJob");
      localStorage.removeItem("bulkApplyJobId");
    }
  }, [dispatch]);

  // controls the visibility of the "Jobs" and "Companies" navigation button in the navbar
  function controlNavigateButtonVisible() {
    // if below tablet - don't show
    if (desktopSize) {
      return true;
    } else {
      return false;
    }
  }

  function conditionShowCountryToggle() {
    if (!isMobile) {
      return true;
    } else {
      return false;
    }
  }

  const posthog = usePostHog();

  function handleLocationHref(type, buttonText) {
    const currentUrl = window.location.hostname;

    const isStaging = currentUrl.includes("staging");

    if (type === "login") {
      const elpUrl =
        isStaging || process.env.NEXT_PUBLIC_JSW_ENV !== "prod"
          ? "https://staging-employer.hiredly.com/login"
          : "https://employer.hiredly.com/login";

      openEmployerSignUp({
        posthog,
        buttonCTA: buttonText,
        buttonColour: buttonText == "Employer Sign Up" ? "purple" : "white",
        section: "navbar",
      });

      // router.replace(elpUrl);
    } else {
      if (process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "sg")
        router.replace("http://hub.hiredly.com/hiredly-sg-employer-waitlist");
      else router.replace(setEmployerParams());
    }
  }

  const utm = getUtmQueryString();

  return (
    <Fragment>
      {/* boolean needs to be string because transient prop cannot have boolean */}
      <AppbarStyled $employer={true}>
        <ToolbarStyled $employer={true}>
          <NavbarContentContainer>
            {/* Logo and navigation buttons */}
            <VanillaGrid
              display={"flex"}
              gap={"16px"}
              alignItems={"center"}
              width={"auto"}
            >
              <VanillaGrid
                sx={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {/* Hamburger menu */}
                {!desktopSize ? (
                  <VanillaGrid
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButtonStyled
                      id="mobile-open-drawer"
                      edge="start"
                      onClick={() => setOpenDrawer(true)}
                      sx={{
                        padding: 0,
                        margin: 0,
                        width: "20px",
                        height: "20px",
                      }}
                    >
                      <Bars3Icon width={20} height={20} color="white" />
                    </IconButtonStyled>
                  </VanillaGrid>
                ) : null}
                {((isMobile && !showSearchbar) ||
                  (!isMobile && showSearchbar) ||
                  !showSearchbar) && (
                  <VanillaGrid
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: mobileLSize && "100%",
                    }}
                  >
                    <VanillaGrid
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "7.258px",
                        height: "fit-content",
                      }}
                    >
                      <VanillaGrid container width={"100%"}>
                        <a href={"/employer"} style={{ display: "flex" }}>
                          {envCountry == "SG" ? (
                            <>
                              <ELPHiredlyLogoSG
                                id="hiredly-logo-button"
                                draggable={false}
                                alt="hiredly-logo"
                                origin={"navbar"}
                              />
                            </>
                          ) : (
                            <>
                              <ELPHiredlyLogo
                                id="hiredly-logo-button"
                                draggable={false}
                                alt="hiredly-logo"
                                origin={"navbar"}
                              />
                            </>
                          )}
                        </a>
                      </VanillaGrid>
                    </VanillaGrid>
                  </VanillaGrid>
                )}
              </VanillaGrid>
              {controlNavigateButtonVisible() ? (
                <Fragment>
                  <VanillaGrid
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",

                      "& > a": {
                        color: "inherit",
                        textDecoration: "none",
                      },

                      "& > a:hover": {
                        textDecoration: "none",
                      },

                      "& > a:visited": {
                        color: "inherit",
                        textDecoration: "none",
                      },
                    }}
                  >
                    <Link
                      href={`/employer/ad-solutions${utm}`}
                     
                     
                    >
                      <NavbarLinksStyled
                        id="solutions-tab"
                        $active={router.pathname === "/employer/ad-solutions"}
                        $employer={true}
                        onClick={() =>
                          sendTrackingEvent({
                            event: "CE_elp_solutions_and_pricing_click",
                            section: "navbar",
                          })
                        }
                      >
                        Ad Solutions
                      </NavbarLinksStyled>
                    </Link>

                    <Link href={`/employer/digital-headhunter${utm}`}>
                      <NavbarLinksStyled
                        id="digital-headhunter-tab"
                        $active={router.pathname === "/employer/digital-headhunter"}
                        $employer={true}
                      >
                        Digital Headhunter
                      </NavbarLinksStyled>
                    </Link>

                    {!hidePostForFree && (
                      <>
                        <Link
                          href={`/employer/retail-fnb-hospitality${utm}`}
                         
                         
                        >
                          <NavbarLinksStyled
                            id="retail-fnb-hospitality-tab"
                            $active={
                              router.pathname ===
                              "/employer/retail-fnb-hospitality"
                            }
                            $employer={true}
                            onClick={() =>
                              sendTrackingEvent({
                                event: "CE_elp_retail_fnb_hospitality_click",
                                section: "navbar",
                              })
                            }
                          >
                            Retail, F&B & Hospitality
                          </NavbarLinksStyled>
                        </Link>
                      </>
                    )}

                    <Link 
                      href="/employer/market-insights" 
                      
                     
                    >
                      <NavbarLinksStyled
                        id="market-insights-tab"
                        $active={
                          router.pathname === 
                          "/employer/market-insights"
                        }
                        $employer={true}
                      >
                        Market Insights
                      </NavbarLinksStyled>
                    </Link>

                    <Link href="/hiredly-x">
                      <NavbarLinksStyled
                        id="companies-hiredly-x-tab"
                        $employer={true}
                        target="_blank"
                        onClick={() =>
                          sendTrackingEvent({
                            event: "CE_elp_hiredly_x_redirect_click",
                            section: "navbar",
                          })
                        }
                      >
                        Agency Service
                      </NavbarLinksStyled>
                    </Link>
                  </VanillaGrid>
                </Fragment>
              ) : null}
            </VanillaGrid>

            <Fragment>
              {!isMobile && (
                <CTAContainer>
                  {conditionShowCountryToggle() && tabletSize && (
                    <ClickAwayListenerStyled
                      onClickAway={() =>
                        !openDrawer && setOpenCountryPopper(false)
                      }
                    >
                      <LocationContainer
                        onClick={() => setOpenCountryPopper(!openCountryPopper)}
                        ref={countryAnchorRef}
                        $employer={true}
                      >
                        <StyledMapPin style={{ color: "white" }} />
                        <CountryText style={{ color: "white" }}>
                          {urlSubdomain.includes("sg") ? "SG" : "MY"}
                        </CountryText>
                        <CountryTogglePopper
                          countryPopBool={openCountryPopper}
                          countryAnchorRef={countryAnchorRef}
                          subdomain={urlSubdomain}
                          router={router}
                        />
                      </LocationContainer>
                    </ClickAwayListenerStyled>
                  )}
                  <VanillaGrid
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <StyledLoginButton
                      $employer={true}
                      variant="text"
                      id="employer-login-button"
                      onClick={() =>
                        handleLocationHref("login", "Employer Log In")
                      }
                      style={{
                        border: "1px solid #EFEFFD",
                        color: "#EFEFFD",
                        background: "transparent",
                        borderRadius: "8px",
                        fontWeight: "500 !important",

                        ":hover": {
                          background: "transparent",
                        },
                      }}
                    >
                      Employer Log in
                    </StyledLoginButton>

                    <StyledSignUpButton
                      $employer={true}
                      onClick={() =>
                        process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "sg"
                          ? "http://hub.hiredly.com/hiredly-sg-employer-waitlist"
                          : handleLocationHref("login", "Employer Sign Up")
                      }
                      variant="text"
                      id="start-hiring-today-button"
                      style={{
                        border: "1px solid #EFEFFD",
                        color: "#120146",
                        background: "#EFEFFD",
                        borderRadius: "8px",
                        fontWeight: "500 !important",

                        ":hover": {
                          background: "#EFEFFD",
                        },
                      }}
                    >
                      Employer Sign Up
                    </StyledSignUpButton>
                  </VanillaGrid>
                  <DividerLine />
                  <StyledOutlinedButton
                    onClick={() => {
                      router.push("/jobs");
                    }}
                    style={{
                      background: "#120146",
                      borderRadius: "100px",
                      boxShadow:
                        "4px 4px 5px 0 rgba(174, 174, 192, 0.40) inset, -4px -4px 4px 0 #0B051E inset",
                      color: "#EFEFFD",
                    }}
                  >
                    Find Jobs
                  </StyledOutlinedButton>
                </CTAContainer>
              )}
            </Fragment>
            {/* </VanillaGrid> */}
            {isMobile &&
              router.pathname.includes("/employer") && ( //maxwidth 640px [AND] current page == employer
                <StyledLoginButton
                  onClick={() => {
                    handleLocationHref("login", "Employer Log In");
                  }}
                  style={{
                    border: "1px solid #EFEFFD",
                    color: "#EFEFFD",
                    background: "transparent",
                    borderRadius: "8px",
                    fontWeight: "500 !important",

                    ":hover": {
                      background: "transparent",
                    },
                  }}
                >
                  Employer Log in
                </StyledLoginButton>
              )}
          </NavbarContentContainer>
        </ToolbarStyled>

        {/* popup drawer for tablet and below */}
        <Drawer
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "640px",
              gap: "40px",
              padding: "24px",

              "@media(max-width: 767px)": {
                padding: "24px 24px 40px 24px",
              },
            },
          }}
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
        >
          <VanillaGrid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "stretch",
            }}
          >
            <VanillaGrid
              sx={{ display: "flex", alignItems: "baseline", gap: "7.258px" }}
            >
              <VanillaGrid
                container
                alignItems={"end"}
                columnGap={"5px"}
                width={"100%"}
              >
                <a href={"/employer"} style={{ display: "flex" }}>
                  {envCountry == "SG" ? (
                    <>
                      <ELPDrawerHiredlyLogoSG
                        id="hiredly-logo-button"
                        draggable={false}
                        alt="hiredly-logo"
                      />
                    </>
                  ) : (
                    <>
                      <ELPDrawerHiredlyLogo
                        id="hiredly-logo-button"
                        draggable={false}
                        alt="hiredly-logo"
                      />
                    </>
                  )}
                </a>
              </VanillaGrid>
            </VanillaGrid>
            <IconButtonStyled
              onClick={() => setOpenDrawer(false)}
              sx={{ padding: 0 }}
            >
              <XMarkIcon width={32} height={32} color="#222222" />
            </IconButtonStyled>
          </VanillaGrid>
          <DrawerContentContainer>
            <VanillaGrid>
              <StyledMenuText>employers</StyledMenuText>
            </VanillaGrid>

            <LinkStyled
              href={`/employer/ad-solutions${utm}`}
              onClick={() =>
                sendTrackingEvent({
                  event: "CE_elp_solutions_and_pricing_click",
                  section: "sidebar",
                })
              }
              width={"100%"}
            >
              <LinkContainer container>Ad Solutions</LinkContainer>
            </LinkStyled>

            <LinkStyled
              href={`/employer/digital-headhunter${utm}`}
              width={"100%"}
            >
              <LinkContainer container>Digital Headhunter</LinkContainer>
            </LinkStyled>

            {!hidePostForFree && (
              <>
                <LinkStyled
                  href={`/employer/retail-fnb-hospitality${utm}`}
                  onClick={() =>
                    sendTrackingEvent({
                      event: "CE_elp_retail_fnb_hospitality_click",
                      section: "sidebar",
                    })
                  }
                  width={"100%"}
                >
                  <LinkContainer container>Retail, F&B & Hospitality</LinkContainer>
                </LinkStyled>
              </>
            )}

            <LinkStyled href="/employer/market-insights" width={"100%"}>
              <LinkContainer container>Market Insights</LinkContainer>
            </LinkStyled>

            <LinkStyled
              href="/hiredly-x"
              onClick={() =>
                sendTrackingEvent({
                  event: "CE_elp_hiredly_x_redirect_click",
                  section: "sidebar",
                })
              }
              width={"100%"}
              target="_blank"
            >
              <LinkContainer container>Agency Service</LinkContainer>
            </LinkStyled>

            <VanillaGrid
              container
              justifyContent={"flex-start"}
              sx={{ gap: "8px" }}
              maxWidth={"400px"}
            >
              <DrawerLoginButton
                variant="text"
                id="employer-login-button"
                onClick={() => handleLocationHref("login", "Log In")}
                style={{
                  borderRadius: "8px",
                }}
              >
                Log in
              </DrawerLoginButton>
              <DrawerSignUpButton
                onClick={() =>
                  process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "sg"
                    ? "http://hub.hiredly.com/hiredly-sg-employer-waitlist"
                    : handleLocationHref("login", "Sign Up")
                }
                variant="text"
                id="start-hiring-today-button"
                style={{
                  borderRadius: "8px",
                }}
              >
                Sign Up
              </DrawerSignUpButton>
            </VanillaGrid>

            <HorizontalDividerLine />

            <VanillaGrid>
              <StyledMenuText>jobseekers</StyledMenuText>
            </VanillaGrid>

            <VanillaGrid container>
              <LinkStyled href="/jobs" width={"100%"}>
                <LinkContainer container>Find Jobs</LinkContainer>
              </LinkStyled>
            </VanillaGrid>

            <HorizontalDividerLine />

            {/* COUNTRY TOGGLE STARTS HERE */}
            <VanillaGrid container>
              <DrawerCountryOption
                mobileSize={mobileSize}
                openCountryPopper={openCountryPopper}
                setOpenCountryPopper={setOpenCountryPopper}
                subdomain={urlSubdomain}
                router={router}
              />
              {/* COUNTRY TOGGLE ENDS HERE */}
            </VanillaGrid>
          </DrawerContentContainer>
        </Drawer>

        <CustomBreadcrumbs items={breadcrumbItems} />
      </AppbarStyled>

      {/* Snackbar declaration */}
      <SharedSnackbar />
    </Fragment>
  );
}

function CountryTogglePopper({ countryPopBool, countryAnchorRef, router }) {
  return (
    <PopperStyled
      open={countryPopBool}
      anchorEl={countryAnchorRef.current}
      role={undefined}
      transition
      disablePortal
    >
      {({ TransitionProps }) => (
        <CountryGrowStyled {...TransitionProps}>
          <PaperStyled>
            <MenuListStyled width={"100%"}>
              <CountryMenuItem
                onClick={() =>
                  handleSwitchCountry({ country: "my", router: router })
                }
              >
                <VanillaGrid
                  sx={{
                    display: "flex",
                    width: "24px",
                    height: "24px",
                    padding: "3.429px 0px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <MYCountryFlag isPopper />
                </VanillaGrid>
                <CountryOptionName variant="body1">MY</CountryOptionName>
              </CountryMenuItem>
              <CountryMenuItem
                onClick={() =>
                  handleSwitchCountry({ country: "sg", router: router })
                }
              >
                <SGCountryFlag isPopper />
                <CountryOptionName variant="body1">SG</CountryOptionName>
              </CountryMenuItem>
            </MenuListStyled>
          </PaperStyled>
        </CountryGrowStyled>
      )}
    </PopperStyled>
  );
}

function DrawerCountryOption({
  mobileSize,
  openCountryPopper,
  setOpenCountryPopper,
  subdomain,
  router,
}) {
  return (
    <DrawerCountryToggleGrid container mobileSize={mobileSize}>
      {/* dropdown current option */}
      <VanillaGrid
        container
        sx={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
          width: "100%",
        }}
        onClick={() => {
          setOpenCountryPopper(!openCountryPopper);
        }}
        zIndex={100}
      >
        <VanillaGrid
          sx={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <StyledMapPin />
          {!subdomain.includes("sg") ? (
            <DrawerCountryText>Malaysia</DrawerCountryText>
          ) : (
            <DrawerCountryText>Singapore</DrawerCountryText>
          )}

          {/* {!subdomain.includes("sg") ? "Malaysia" : "Singapore"} */}
        </VanillaGrid>
        {openCountryPopper ? <ChevronUpStyled /> : <ChevronDownStyled />}
      </VanillaGrid>

      <VanillaGrid
        sx={{
          display: "flex",
          padding: "0px 24px",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "24px",
        }}
      >
        {/* Malaysia option */}
        <VanillaGrid
          container
          display={openCountryPopper ? "flex" : "none"}
          sx={{
            cursor: "pointer",
            width: "109px",
            alignItems: "center",
            gap: "18px",
            ":hover": {
              backgroundColor: Color.lightGrey,
            },
          }}
          onClick={() => handleSwitchCountry({ country: "my", router: router })}
          flexWrap={"nowrap"}
        >
          <DrawerDropdownRadio checked={!subdomain.includes("sg")} />
          <VanillaGrid
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flex: "1 0 0",
            }}
          >
            <VanillaGrid
              sx={{
                display: "flex",
                width: "24px",
                height: "24px",
                padding: "3.429px 0px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MYCountryFlag isPopper />
            </VanillaGrid>
            Malaysia
          </VanillaGrid>
        </VanillaGrid>

        {/* Singapore option */}
        <VanillaGrid
          container
          display={openCountryPopper ? "flex" : "none"}
          sx={{
            cursor: "pointer",
            width: "109px",
            alignItems: "center",
            gap: "18px",
            ":hover": {
              backgroundColor: Color.lightGrey,
            },
          }}
          onClick={() => handleSwitchCountry({ country: "sg", router: router })}
          flexWrap={"nowrap"}
        >
          <DrawerDropdownRadio checked={subdomain.includes("sg")} />
          <VanillaGrid
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flex: "1 0 0",
            }}
          >
            <VanillaGrid
              sx={{
                display: "flex",
                width: "24px",
                height: "24px",
                padding: "3.429px 0px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <SGCountryFlag isPopper />
            </VanillaGrid>
            Singapore
          </VanillaGrid>
        </VanillaGrid>
      </VanillaGrid>
    </DrawerCountryToggleGrid>
  );
}

export default WithOnboarding(EmployerNavbar);
